import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Video, ShoppingCart, ShoppingBag, Play, Clock, Lock, Users, Eye, Globe, EyeOff, X } from "lucide-react";
import { api, money } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const fmtOffset = (s) => {
  const m = Math.floor((s || 0) / 60), sec = (s || 0) % 60;
  return `${m}:${String(sec).padStart(2, "0")}`;
};

export default function ReplayView({ sessionId }) {
  const { user, authApi } = useAuth();
  const { addItem, count, checkout } = useCart();
  const [replay, setReplay] = useState(undefined); // undefined=loading, null=unavailable
  const [showShop, setShowShop] = useState(false);
  const videoRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/live/sessions/${sessionId}/replay`); setReplay(data); }
    catch { setReplay(null); }
  }, [sessionId]);
  useEffect(() => { load(); }, [load]);

  // Count a replay view once on open.
  useEffect(() => { if (replay?.id) api.post(`/live/sessions/${sessionId}/replay/view`).catch(() => {}); }, [replay?.id, sessionId]);

  const track = (event, handle) => api.post(`/live/sessions/${sessionId}/track`, { event, handle, source: "replay" }).catch(() => {});

  const revalidate = async (handle) => {
    try { const { data } = await api.get(`/live/sessions/${sessionId}/product/${encodeURIComponent(handle)}/revalidate`); return data; }
    catch { return null; }
  };
  const addToCart = async (p) => {
    if (!p?.variantId) { toast.error("Product unavailable"); return; }
    const fresh = await revalidate(p.handle);
    const avail = fresh ? (fresh.available && !fresh.blocked) : p.available;
    if (!avail) { toast.error("This item is no longer available."); return; }
    const use = fresh ? { ...p, price: fresh.price ?? p.price, variantId: fresh.variantId || p.variantId } : p;
    if (addItem({ defaultVariantId: use.variantId, handle: use.handle, title: use.title, image: use.image, price: use.price, currency: use.currency })) {
      toast.success("Added to cart"); track("add_to_cart", use.handle);
    }
  };
  const buyNow = async (p) => {
    if (!p?.variantId) { toast.error("Product unavailable"); return; }
    const fresh = await revalidate(p.handle);
    const avail = fresh ? (fresh.available && !fresh.blocked) : p.available;
    if (!avail) { toast.error("This item is no longer available."); return; }
    const use = fresh ? { ...p, price: fresh.price ?? p.price, variantId: fresh.variantId || p.variantId } : p;
    track("checkout", use.handle);
    const url = await checkout({ creator_email: replay.host_email, seller_email: replay.host_email, session_id: sessionId, product_handle: use.handle, price: use.price, viewer_email: user?.email, source: "replay" }, [{ variantId: use.variantId, quantity: 1 }]);
    if (url) window.location.href = url;
  };

  const seek = (offset, handle) => {
    if (videoRef.current) { videoRef.current.currentTime = offset || 0; videoRef.current.play().catch(() => {}); }
    track("click", handle);
  };

  const setVisibility = async (visibility) => {
    try { await authApi.put(`/live/sessions/${sessionId}/replay-visibility`, { visibility }); setReplay((r) => ({ ...r, visibility })); toast.success(`Replay set to ${visibility}.`); }
    catch { toast.error("Could not update visibility."); }
  };

  if (replay === undefined) return <div className="py-24 text-center text-muted-foreground bg-taven-black min-h-screen">Loading replay…</div>;
  if (replay === null) return (
    <div className="bg-taven-black min-h-screen text-white flex flex-col items-center justify-center gap-4 px-6" data-testid="replay-unavailable">
      <Lock className="w-12 h-12 text-taven-gold/60" />
      <p className="font-display font-black text-lg">This replay isn't available</p>
      <p className="text-white/50 text-sm text-center max-w-sm">It may be private, followers-only, or removed by moderation.</p>
      <Link to="/live" className="bg-taven-gold text-black px-5 py-2.5 text-xs font-bold uppercase tracking-wider">Back to Live</Link>
    </div>
  );

  const isHost = user && replay.host_email === user.email;
  const products = replay.products || [];
  const timeline = replay.timeline || [];
  const rec = replay.recording || {};

  return (
    <div className="bg-taven-black min-h-screen text-white" data-testid="replay-view">
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <Link to="/live" className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white/70 hover:text-white"><ArrowLeft className="w-4 h-4" /> Back</Link>
        <div className="flex items-center gap-2 text-sm">
          <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-white/10 px-2 py-1"><Video className="w-3 h-3" /> Replay</span>
          <span className="font-bold line-clamp-1">{replay.title}</span>
        </div>
        <span className="flex items-center gap-1 text-xs text-white/60"><Eye className="w-3.5 h-3.5" /> {replay.likes || 0}</span>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-0 max-w-7xl mx-auto">
        <div className="relative bg-black aspect-[9/16] sm:aspect-video lg:h-[70vh] flex items-center justify-center" data-testid="replay-stage">
          {rec.available ? (
            <video ref={videoRef} src={rec.playback_url} controls playsInline className="w-full h-full object-contain bg-black" data-testid="replay-video" />
          ) : (
            <div className="flex flex-col items-center justify-center gap-3 text-center px-6" data-testid="replay-no-recording">
              <div className="w-20 h-20 rounded-full bg-taven-gold/10 border-2 border-taven-gold flex items-center justify-center"><Play className="w-8 h-8 text-taven-gold" /></div>
              <p className="font-display font-black">Replay video isn't ready</p>
              <p className="text-white/50 text-sm max-w-xs">
                {rec.status === "processing" ? "This recording is still processing — check back soon." : "A recorded video isn't available for this session, but you can still shop everything that was featured live."}
              </p>
            </div>
          )}
          {products.length > 0 && (
            <button onClick={() => setShowShop(true)} data-testid="replay-shop-btn" className="absolute right-3 bottom-4 flex flex-col items-center gap-1 text-white z-20">
              <span className="w-12 h-12 rounded-full bg-taven-orange hover:bg-[#d9561a] flex items-center justify-center relative"><ShoppingBag className="w-5 h-5" /><span className="absolute -top-1 -right-1 bg-taven-gold text-black text-[10px] font-black min-w-[18px] h-[18px] flex items-center justify-center rounded-full px-1">{products.length}</span></span>
              <span className="text-[10px] font-bold">Shop</span>
            </button>
          )}
        </div>

        <div className="flex flex-col lg:h-[70vh] border-l border-white/10 overflow-y-auto">
          {isHost && (
            <div className="px-4 py-3 border-b border-white/10" data-testid="replay-visibility-control">
              <p className="text-[10px] font-bold uppercase tracking-wider text-taven-gold mb-2">Replay Visibility</p>
              <div className="grid grid-cols-3 gap-1.5">
                {[{ k: "public", l: "Public", i: Globe }, { k: "followers", l: "Followers", i: Users }, { k: "private", l: "Private", i: EyeOff }].map((o) => (
                  <button key={o.k} onClick={() => setVisibility(o.k)} data-testid={`replay-vis-${o.k}`}
                    className={`flex flex-col items-center gap-1 py-2 border text-[10px] font-bold uppercase ${replay.visibility === o.k ? "border-taven-gold bg-taven-gold/15 text-taven-gold" : "border-white/15 text-white/60 hover:border-white/40"}`}>
                    <o.i className="w-3.5 h-3.5" />{o.l}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="px-4 py-3 border-b border-white/10 text-xs font-bold uppercase tracking-wider text-taven-gold flex items-center gap-2"><Clock className="w-4 h-4" /> Shop the Replay</div>
          <div className="flex-1 p-3 space-y-2">
            {timeline.length === 0 && products.length === 0 && <p className="text-white/40 text-sm">No products were featured in this session.</p>}
            {(timeline.length ? timeline : products).map((p) => (
              <div key={p.handle} className="flex items-center gap-3 border border-white/10 p-2" data-testid={`replay-product-${p.handle}`}>
                {p.image && <img src={p.image} alt="" className="w-12 h-12 object-cover" />}
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold line-clamp-1">{p.title}</p>
                  <p className="text-xs text-taven-orange font-black">{money(p.price)}</p>
                  {rec.available && "offset" in p && (
                    <button onClick={() => seek(p.offset, p.handle)} data-testid={`replay-seek-${p.handle}`} className="mt-1 flex items-center gap-1 text-[10px] font-bold text-taven-gold hover:underline"><Play className="w-3 h-3" /> Watch at {fmtOffset(p.offset)}</button>
                  )}
                </div>
                <div className="flex flex-col gap-1">
                  <button onClick={() => buyNow(p)} data-testid={`replay-buy-${p.handle}`} className="bg-taven-orange text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-[#d9561a]">Buy</button>
                  <button onClick={() => addToCart(p)} data-testid={`replay-add-${p.handle}`} className="bg-white/10 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-taven-gold hover:text-black">Add</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showShop && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-end sm:items-center justify-center" onClick={() => setShowShop(false)}>
          <div className="bg-white text-black w-full sm:max-w-md max-h-[80vh] overflow-y-auto p-5 rounded-t-2xl sm:rounded-none" onClick={(e) => e.stopPropagation()} data-testid="replay-shop-drawer">
            <div className="flex items-center justify-between mb-4"><h3 className="font-black font-display flex items-center gap-2"><ShoppingBag className="w-5 h-5 text-taven-orange" /> Shop this Replay</h3><button onClick={() => setShowShop(false)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-3">
              {products.map((p) => (
                <div key={p.handle} className="flex items-center gap-3 border border-black/10 p-2">
                  {p.image && <img src={p.image} alt="" className="w-16 h-16 object-cover" />}
                  <div className="flex-1 min-w-0"><p className="text-sm font-bold line-clamp-2">{p.title}</p><p className="text-sm text-taven-orange font-black">{money(p.price)}</p></div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => buyNow(p)} className="bg-taven-orange text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-[#d9561a]">Buy Now</button>
                    <button onClick={() => addToCart(p)} className="bg-taven-black text-white text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 hover:bg-taven-gold hover:text-black">Add</button>
                  </div>
                </div>
              ))}
            </div>
            <Link to="/cart" className="w-full mt-4 flex items-center justify-center gap-2 border border-taven-black text-taven-black text-xs font-bold uppercase tracking-wider py-2.5 hover:bg-taven-black hover:text-white"><ShoppingCart className="w-4 h-4" /> View Cart ({count})</Link>
          </div>
        </div>
      )}
    </div>
  );
}
